// The shape of her mouth, from the sound of her voice - a light formant-based
// lip-sync that runs on one FFT frame at a time.
//
// Vowels are told apart by the first two resonances of the vocal tract, the
// formants:
//   F1 rises as the jaw opens:     "ee", "oo" ~300 Hz, "e", "o" ~500, "aa" ~750
//   F2 rises as the lips spread:   "oo" ~800 Hz, "o" ~1000, "aa" ~1250,
//                                  "e" ~1900, "ee" ~2300
// and sibilants ("s", "sh", "f") are mostly noise above 3.5 kHz with little
// voicing under it - a nearly closed mouth with the teeth together.
//
// So: loudness decides how much the mouth moves at all, the F1 peak sets how far
// the jaw drops, the F2 peak sets wide-versus-round, and a hiss closes it. The
// result is { open, wide }, both 0..1; the renderer springs towards it, which
// smooths frame-to-frame jitter the way real lips smooth between sounds.

export const REST = Object.freeze({ open: 0, wide: 0.5 });

const clamp01 = (n) => Math.max(0, Math.min(1, n));

// A moving average over ~250 Hz. Speech is a comb of harmonics every ~200 Hz;
// smoothing it leaves the formant envelope, whose peaks are what we want.
function envelope(bins, binHz) {
	const half = Math.max(1, Math.round(125 / binHz));
	const out = new Float32Array(bins.length);
	let sum = 0;
	let n = 0;
	for (let i = 0; i < Math.min(bins.length, half); i++) {
		sum += bins[i];
		n++;
	}
	for (let i = 0; i < bins.length; i++) {
		const add = i + half;
		if (add < bins.length) {
			sum += bins[add];
			n++;
		}
		const drop = i - half - 1;
		if (drop >= 0) {
			sum -= bins[drop];
			n--;
		}
		out[i] = sum / n;
	}
	return out;
}

// The strongest point of the envelope in lo..hi, refined to the weighted centre
// of the hump around it. The raw maximum snaps to whichever harmonic is nearest,
// which is up to half the voice's pitch (~100 Hz) off the real formant.
function peakHz(env, binHz, lo, hi) {
	const a = Math.max(0, Math.floor(lo / binHz));
	const b = Math.min(env.length - 1, Math.ceil(hi / binHz));
	let best = a;
	for (let i = a + 1; i <= b; i++) if (env[i] > env[best]) best = i;
	const reach = Math.round(300 / binHz);
	const from = Math.max(a, best - reach);
	const to = Math.min(b, best + reach);
	let low = env[best];
	for (let i = from; i <= to; i++) low = Math.min(low, env[i]);
	let w = 0;
	let m = 0;
	for (let i = from; i <= to; i++) {
		const p = (env[i] - low) ** 2;
		w += p;
		m += p * i;
	}
	return { hz: (w > 0 ? m / w : best) * binHz, level: env[best] };
}

function meanOver(bins, binHz, lo, hi) {
	const a = Math.max(0, Math.floor(lo / binHz));
	const b = Math.min(bins.length - 1, Math.ceil(hi / binHz));
	let s = 0;
	for (let i = a; i <= b; i++) s += bins[i];
	return s / Math.max(1, b - a + 1);
}

/**
 * @param bins   frequency magnitudes, e.g. from AnalyserNode.getByteFrequencyData
 *               (0..255, dB-scaled) - any array-like of numbers works
 * @param binHz  width of one bin: sampleRate / fftSize
 * @param level  overall loudness of the frame, 0..1
 * @returns {{open: number, wide: number}}
 */
export function visemeFromSpectrum(bins, binHz, level) {
	if (!bins || !bins.length || !(binHz > 0) || !(level > 0.02)) return { ...REST };

	const env = envelope(bins, binHz);
	const f1 = peakHz(env, binHz, 250, 1000);
	// F2 sits well above F1; searching from just past it stops F1's own slope
	// being mistaken for F2 in the back vowels, where the two are close.
	const f2 = peakHz(env, binHz, Math.max(700, f1.hz + 250), 2800);

	// How much of the energy is hiss rather than voice.
	const voiced = meanOver(bins, binHz, 100, 1000);
	const hiss = meanOver(bins, binHz, 3500, 7500);
	const sib = clamp01((hiss - voiced * 0.75) / 60);

	const jaw = clamp01((f1.hz - 300) / 450); // "ee"/"oo" ~0 .. "aa" ~1
	const spread = clamp01((f2.hz - 850) / 1350); // "oo" ~0 .. "ee" ~1

	const loud = Math.pow(clamp01(level), 0.8);
	const open = clamp01(loud * (0.3 + 0.7 * jaw) * (1 - 0.8 * sib));
	const wide = clamp01(spread + (0.75 - spread) * sib);
	return { open, wide };
}
